import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Loader from "../components/common/Loader";
import Button from "../components/common/Button";
import BookingForm from "../components/booking/BookingForm";
import BookingSummary from "../components/booking/BookingSummary";
import * as serviceService from "../services/serviceService";
import * as bookingService from "../services/bookingService";
import * as availabilityService from "../services/availabilityService";
import { hasValidSession } from "../services/authService";
import {
  BOOKING_OWNER_TYPE,
  HENNA_TYPE_OPTIONS,
  HENNA_TYPES,
  JAGUAR_HENNA_DISCLAIMER,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
  TIME_SLOT_INTERVAL_MINUTES,
} from "../utils/constants";
import {
  calculateBookingTotals,
  validateBookingPayload,
} from "../utils/validators";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";

const STUDIO_LOCATION =
  "The artist will email or text the studio address within 48 hours before your appointment.";

const emptyOnsiteAddress = () => ({
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
});

const createEmptyBookingItem = () => ({
  serviceId: "",
  hennaType: "",
  numOfAdults: 1,
  numOfChildren: 0,
  partySize: 1,
  bookingDurationMinutes: 0,
  bookingPrice: 0,
  sortOrder: 0,
});

const initialFormData = {
  bookingItems: [createEmptyBookingItem()],
  bookingDate: "",
  startTime: "",
  endTime: "",
  notes: "",
  serviceMode: SERVICE_MODE.STUDIO,
  locationNotes: "",
  onsiteAddress: emptyOnsiteAddress(),
  guest: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  },
};

const safeCallGetServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }
  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }
  if (typeof serviceService.fetchServices === "function") {
    return serviceService.fetchServices();
  }
  throw new Error("Service list method was not found in serviceService.");
};

const safeCallValidateAvailability = async ({
  bookingDate,
  startTime,
  endTime,
}) => {
  if (typeof availabilityService.validateAvailability === "function") {
    return availabilityService.validateAvailability(
      bookingDate,
      startTime,
      endTime
    );
  }
  if (typeof availabilityService.checkAvailability === "function") {
    return availabilityService.checkAvailability(
      bookingDate,
      startTime,
      endTime
    );
  }
  if (typeof availabilityService.validateBookingTime === "function") {
    return availabilityService.validateBookingTime(
      bookingDate,
      startTime,
      endTime
    );
  }
  throw new Error(
    "Availability validation method was not found in availabilityService."
  );
};

const safeCallGetAvailabilityBetween = async (start, end) => {
  if (typeof availabilityService.getAvailabilityBetweenDates === "function") {
    return availabilityService.getAvailabilityBetweenDates(start, end);
  }
  throw new Error("Availability between dates method was not found.");
};

const safeCallGetAvailabilityByDate = async (date) => {
  if (typeof availabilityService.getAvailabilityByDate === "function") {
    return availabilityService.getAvailabilityByDate(date);
  }
  throw new Error("Availability by date method was not found.");
};

const safeCallGetBookingsByDate = async (date) => {
  if (typeof bookingService.getBookingsByDate === "function") {
    return bookingService.getBookingsByDate(date);
  }
  throw new Error("Bookings by date method was not found.");
};

const safeCallCreateUserBooking = async (payload) => {
  if (typeof bookingService.createUserBooking === "function") {
    return bookingService.createUserBooking(payload);
  }
  if (typeof bookingService.bookAsUser === "function") {
    return bookingService.bookAsUser(payload);
  }
  if (typeof bookingService.createBookingForUser === "function") {
    return bookingService.createBookingForUser(payload);
  }
  throw new Error("User booking method was not found in bookingService.");
};

const safeCallCreateGuestBooking = async (payload) => {
  if (typeof bookingService.createGuestBooking === "function") {
    return bookingService.createGuestBooking(payload);
  }
  if (typeof bookingService.bookAsGuest === "function") {
    return bookingService.bookAsGuest(payload);
  }
  if (typeof bookingService.createBookingForGuest === "function") {
    return bookingService.createBookingForGuest(payload);
  }
  throw new Error("Guest booking method was not found in bookingService.");
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const formatDateToApi = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const timeToMinutes = (timeString) => {
  const [h, m] = String(timeString).slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes) => {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const overlaps = (startA, endA, startB, endB) => startA < endB && endA > startB;

const buildAddressText = (onsiteAddress) => {
  if (!onsiteAddress) return "";

  return [
    onsiteAddress.line1,
    onsiteAddress.line2,
    onsiteAddress.city,
    onsiteAddress.state,
    onsiteAddress.zip,
  ]
    .filter(Boolean)
    .join(", ");
};

const getDefaultHennaTypeForService = (service) => {
  const existingHennaType = service?.hennaType || "";
  return HENNA_TYPE_OPTIONS.includes(existingHennaType)
    ? existingHennaType
    : HENNA_TYPES.RED_NATURAL;
};

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [services, setServices] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [bookingOwnerType, setBookingOwnerType] = useState(
    BOOKING_OWNER_TYPE.USER
  );
  const [isAuthenticated, setIsAuthenticated] = useState(hasValidSession());

  useEffect(() => {
    const syncAuthState = () => {
      setIsAuthenticated(hasValidSession());
    };

    syncAuthState();

    window.addEventListener("focus", syncAuthState);
    document.addEventListener("visibilitychange", syncAuthState);

    return () => {
      window.removeEventListener("focus", syncAuthState);
      document.removeEventListener("visibilitychange", syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBookingOwnerType(BOOKING_OWNER_TYPE.GUEST);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadServicesAndAvailableDates = async () => {
      try {
        setLoadingServices(true);
        setPageError("");

        const [servicesData, availabilityData] = await Promise.all([
          safeCallGetServices(),
          safeCallGetAvailabilityBetween(
            formatDateToApi(new Date()),
            formatDateToApi(addDays(new Date(), 60))
          ),
        ]);

        if (servicesData?.statusCode && servicesData.statusCode !== 200) {
          throw new Error(servicesData.message || "Failed to load services.");
        }

        if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
          throw new Error(
            availabilityData.message || "Failed to load availability."
          );
        }

        const serviceList = Array.isArray(servicesData?.serviceList)
          ? servicesData.serviceList
          : [];
        const activeServices = serviceList.filter(
          (service) => service?.active !== false
        );
        setServices(activeServices);

        const rules = Array.isArray(availabilityData?.availabilityRuleList)
          ? availabilityData.availabilityRuleList
          : [];

        const uniqueDates = [
          ...new Set(
            rules
              .filter((rule) => rule?.isActive !== false && rule?.date)
              .map((rule) => rule.date)
          ),
        ];

        setAvailableDates(uniqueDates);

        const serviceIdFromQuery = searchParams.get("serviceId");
        if (serviceIdFromQuery) {
          const matched = activeServices.find(
            (service) => Number(service.id) === Number(serviceIdFromQuery)
          );

          if (matched) {
            setFormData((prev) => ({
              ...prev,
              bookingItems: [
                {
                  ...createEmptyBookingItem(),
                  serviceId: String(matched.id),
                  hennaType: getDefaultHennaTypeForService(matched),
                },
              ],
            }));
          }
        }
      } catch (err) {
        setPageError(err?.message || "Unable to load booking services.");
      } finally {
        setLoadingServices(false);
      }
    };

    loadServicesAndAvailableDates();
  }, [searchParams]);

  const bookingTotals = useMemo(() => {
    return calculateBookingTotals(formData.bookingItems || [], services);
  }, [formData.bookingItems, services]);

  const bookingItemsWithDetails = bookingTotals.items || [];
  const totalPartySize = bookingTotals.totalPartySize || 0;
  const totalBookingPrice = bookingTotals.totalPrice || 0;
  const totalBookingDurationMinutes = bookingTotals.totalDurationMinutes || 0;

  useEffect(() => {
    const nextMode = bookingTotals.serviceMode || SERVICE_MODE.STUDIO;

    setFormData((prev) => {
      const shouldClearLocation =
        nextMode === SERVICE_MODE.STUDIO &&
        (prev.locationNotes ||
          prev.onsiteAddress.line1 ||
          prev.onsiteAddress.line2 ||
          prev.onsiteAddress.city ||
          prev.onsiteAddress.state ||
          prev.onsiteAddress.zip);

      if (prev.serviceMode === nextMode && !shouldClearLocation) {
        return prev;
      }

      return {
        ...prev,
        serviceMode: nextMode,
        locationNotes: nextMode === SERVICE_MODE.STUDIO ? "" : prev.locationNotes,
        onsiteAddress:
          nextMode === SERVICE_MODE.STUDIO
            ? emptyOnsiteAddress()
            : prev.onsiteAddress,
      };
    });

    if (nextMode === SERVICE_MODE.STUDIO) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.locationNotes;
        delete nextErrors.onsiteAddress;
        delete nextErrors.serviceMode;
        return nextErrors;
      });
    }
  }, [bookingTotals.serviceMode]);

  useEffect(() => {
    const loadSlotsForSelectedDate = async () => {
      if (!formData.bookingDate || totalBookingDurationMinutes <= 0) {
        setAvailableSlots([]);
        return;
      }

      try {
        setSlotsLoading(true);
        setAvailabilityMessage("");

        const [availabilityData, bookingsData] = await Promise.all([
          safeCallGetAvailabilityByDate(formData.bookingDate),
          safeCallGetBookingsByDate(formData.bookingDate),
        ]);

        if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
          throw new Error(
            availabilityData.message || "Failed to load date availability."
          );
        }

        if (bookingsData?.statusCode && bookingsData.statusCode !== 200) {
          throw new Error(
            bookingsData.message || "Failed to load bookings for selected date."
          );
        }

        const rules = Array.isArray(availabilityData?.availabilityRuleList)
          ? availabilityData.availabilityRuleList.filter(
              (rule) => rule?.isActive !== false
            )
          : [];

        const bookings = Array.isArray(bookingsData?.bookingList)
          ? bookingsData.bookingList.filter(
              (booking) => booking?.status !== "CANCELED"
            )
          : [];

        const slots = [];

        rules.forEach((rule) => {
          const ruleStart = timeToMinutes(rule.startTime);
          const ruleEnd = timeToMinutes(rule.endTime);

          for (
            let start = ruleStart;
            start + totalBookingDurationMinutes <= ruleEnd;
            start += TIME_SLOT_INTERVAL_MINUTES
          ) {
            const end = start + totalBookingDurationMinutes;

            const isBlocked = bookings.some((booking) => {
              const bookingStart = timeToMinutes(booking.startTime);
              const bookingEnd = timeToMinutes(booking.endTime);
              return overlaps(start, end, bookingStart, bookingEnd);
            });

            if (!isBlocked) {
              slots.push({
                startTime: minutesToTime(start),
                endTime: minutesToTime(end),
              });
            }
          }
        });

        setAvailableSlots(slots);

        if (formData.startTime) {
          const matchingStartSlot = slots.find(
            (slot) => slot.startTime === formData.startTime
          );

          if (matchingStartSlot) {
            setFormData((prev) => ({
              ...prev,
              startTime: matchingStartSlot.startTime,
              endTime: matchingStartSlot.endTime,
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              startTime: "",
              endTime: "",
            }));
          }
        }
      } catch (err) {
        setAvailableSlots([]);
        setAvailabilityMessage(
          err?.message || "Unable to load available time slots."
        );
      } finally {
        setSlotsLoading(false);
      }
    };

    loadSlotsForSelectedDate();
  }, [
    formData.bookingDate,
    totalBookingDurationMinutes,
    formData.startTime,
  ]);

  const resetMessages = () => {
    setPageError("");
    setAvailabilityMessage("");
  };

  const clearCommonErrors = () => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.startTime;
      delete next.endTime;
      delete next.bookingDate;
      delete next.partySize;
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    resetMessages();

    if (name.includes(".")) {
      const [parent, child] = name.split(".");

      setFormData((prev) => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        };

        if (
          parent === "onsiteAddress" &&
          updated.serviceMode === SERVICE_MODE.STUDIO
        ) {
          updated.locationNotes = "";
          updated.onsiteAddress = emptyOnsiteAddress();
        }

        return updated;
      });

      setErrors((prev) => {
        const next = { ...prev };

        if (name.startsWith("onsiteAddress.") && next.onsiteAddress) {
          next.onsiteAddress = {
            ...next.onsiteAddress,
            [child]: "",
          };
        } else {
          delete next[name];
        }

        return next;
      });
    } else {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };

        if (name === "bookingDate") {
          updated.startTime = "";
          updated.endTime = "";
        }

        return updated;
      });

      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    clearCommonErrors();
  };

  const handleItemChange = (index, field, rawValue) => {
    resetMessages();

    setFormData((prev) => {
      const nextItems = [...(prev.bookingItems || [])];
      const existingItem = nextItems[index] || createEmptyBookingItem();

      const value =
        field === "numOfAdults" || field === "numOfChildren"
          ? Math.max(0, Number(rawValue) || 0)
          : rawValue;

      const nextItem = {
        ...existingItem,
        [field]: value,
      };

      if (field === "serviceId") {
        const nextService = services.find(
          (service) => Number(service.id) === Number(rawValue)
        );

        nextItem.hennaType = nextService
          ? getDefaultHennaTypeForService(nextService)
          : "";

        return {
          ...prev,
          bookingItems: nextItems.map((item, itemIndex) =>
            itemIndex === index ? nextItem : item
          ),
          startTime: "",
          endTime: "",
        };
      }

      nextItems[index] = nextItem;

      return {
        ...prev,
        bookingItems: nextItems,
      };
    });

    setErrors((prev) => {
      const next = { ...prev };

      if (Array.isArray(next.bookingItems) && next.bookingItems[index]) {
        next.bookingItems = [...next.bookingItems];
        next.bookingItems[index] = {
          ...next.bookingItems[index],
          [field]: "",
          partySize: "",
        };
      }

      return next;
    });

    clearCommonErrors();
  };

  const handleAddService = () => {
    resetMessages();
    setFormData((prev) => ({
      ...prev,
      bookingItems: [
        ...(prev.bookingItems || []),
        {
          ...createEmptyBookingItem(),
          sortOrder: (prev.bookingItems || []).length,
        },
      ],
      startTime: "",
      endTime: "",
    }));
  };

  const handleRemoveService = (index) => {
    resetMessages();

    setFormData((prev) => {
      const remainingItems = (prev.bookingItems || [])
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          sortOrder: itemIndex,
        }));

      return {
        ...prev,
        bookingItems:
          remainingItems.length > 0
            ? remainingItems
            : [createEmptyBookingItem()],
        startTime: "",
        endTime: "",
      };
    });

    setErrors((prev) => {
      const next = { ...prev };
      if (Array.isArray(next.bookingItems)) {
        next.bookingItems = next.bookingItems.filter(
          (_, itemIndex) => itemIndex !== index
        );
      }
      return next;
    });
  };

  const handleSelectSlot = (slot) => {
    resetMessages();
    setFormData((prev) => ({
      ...prev,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
    setErrors((prev) => ({
      ...prev,
      startTime: "",
      endTime: "",
    }));
  };

  const buildPayload = () => {
    const resolvedEndTime =
      formData.startTime && totalBookingDurationMinutes > 0
        ? minutesToTime(
            timeToMinutes(formData.startTime) + totalBookingDurationMinutes
          )
        : formData.endTime;

    const bookingItems = bookingItemsWithDetails
      .filter((item) => item?.serviceId)
      .map((item, index) => ({
        service: {
          id: Number(item.serviceId),
          hennaType: item.hennaType,
        },
        hennaType: item.hennaType,
        numOfAdults: Number(item.numOfAdults) || 0,
        numOfChildren: Number(item.numOfChildren) || 0,
        partySize: Number(item.partySize) || 0,
        bookingDurationMinutes: Number(item.bookingDurationMinutes) || 0,
        bookingPrice: Number((item.bookingPrice || 0).toFixed(2)),
        sortOrder: index,
      }));

    const firstItem = bookingItems[0] || null;

    const basePayload = {
      numOfAdults: firstItem?.numOfAdults || 0,
      numOfChildren: firstItem?.numOfChildren || 0,
      partySize: totalPartySize,
      bookingDate: formData.bookingDate,
      startTime: formData.startTime,
      endTime: resolvedEndTime,
      bookingDurationMinutes: totalBookingDurationMinutes,
      bookingPrice: Number(totalBookingPrice.toFixed(2)),
      notes: formData.notes?.trim() || "",
      serviceMode: bookingTotals.serviceMode,
      locationNotes:
        bookingTotals.serviceMode === SERVICE_MODE.MOBILE
          ? formData.locationNotes?.trim() || ""
          : "",
      onsiteAddress:
        bookingTotals.serviceMode === SERVICE_MODE.MOBILE
          ? {
              line1: formData.onsiteAddress.line1?.trim() || "",
              line2: formData.onsiteAddress.line2?.trim() || "",
              city: formData.onsiteAddress.city?.trim() || "",
              state: formData.onsiteAddress.state?.trim() || "",
              zip: formData.onsiteAddress.zip?.trim() || "",
            }
          : null,
      hennaType: firstItem?.hennaType || "",
      service:
        firstItem?.service || firstItem?.serviceId
          ? {
              id: Number(firstItem.service.id || firstItem.serviceId),
              hennaType: firstItem.hennaType,
            }
          : null,
      bookingItems,
    };

    if (bookingOwnerType === BOOKING_OWNER_TYPE.GUEST) {
      return {
        ...basePayload,
        guest: {
          firstName: formData.guest.firstName?.trim() || "",
          lastName: formData.guest.lastName?.trim() || "",
          email: formData.guest.email?.trim() || "",
          phoneNumber: formData.guest.phoneNumber?.trim() || "",
        },
      };
    }

    return basePayload;
  };

  const runLocalValidation = () => {
    const validation = validateBookingPayload({
      bookingDate: formData.bookingDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      bookingItems: formData.bookingItems,
      serviceMode: bookingTotals.serviceMode,
      guest:
        bookingOwnerType === BOOKING_OWNER_TYPE.GUEST ? formData.guest : null,
      onsiteAddress:
        bookingTotals.serviceMode === SERVICE_MODE.MOBILE
          ? formData.onsiteAddress
          : null,
    });

    setErrors(validation.errors || {});
    setFormData((prev) => ({
      ...prev,
      serviceMode: bookingTotals.serviceMode,
    }));

    return validation;
  };

  const hasJaguarWithoutLeadTime = () => {
    if (!formData.bookingDate) return false;

    const hasJaguar = bookingItemsWithDetails.some(
      (item) => item?.hennaType === HENNA_TYPES.JAGUAR
    );

    if (!hasJaguar) return false;

    const today = new Date();
    const bookingDate = new Date(`${formData.bookingDate}T00:00:00`);
    const minLeadTime = new Date(today);
    minLeadTime.setDate(today.getDate() + 14);

    return bookingDate < minLeadTime;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetMessages();

    const validation = runLocalValidation();
    if (!validation.isValid) {
      setPageError(
        "Please review the form and correct the highlighted details before submitting."
      );
      return;
    }

    if (hasJaguarWithoutLeadTime()) {
      setPageError(JAGUAR_HENNA_DISCLAIMER);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    const payload = buildPayload();

    try {
      setSubmitting(true);

      const availabilityData = await safeCallValidateAvailability({
        bookingDate: payload.bookingDate,
        startTime: payload.startTime,
        endTime: payload.endTime,
      });

      if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
        throw new Error(
          availabilityData.message || "Availability validation failed."
        );
      }

      if (availabilityData?.available !== true) {
        throw new Error(
          availabilityData?.message ||
            "The selected appointment time is no longer available."
        );
      }

      const data =
        bookingOwnerType === BOOKING_OWNER_TYPE.GUEST
          ? await safeCallCreateGuestBooking(payload)
          : await safeCallCreateUserBooking(payload);

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Booking request failed.");
      }

      const booked = data?.booking || null;
      const confirmationCode =
        data?.bookingConfirmationCode || booked?.bookingConfirmationCode || "";
      const guestEmail = payload?.guest?.email || "";
      const addressText = buildAddressText(payload.onsiteAddress);
      const modeDescription =
        payload.serviceMode === SERVICE_MODE.MOBILE
          ? MOBILE_MODE_DESCRIPTION
          : STUDIO_MODE_DESCRIPTION;

      const bookedServicesText = bookingItemsWithDetails
        .map((item) => {
          const serviceName = item?.service?.serviceName || "Selected service";
          return `${serviceName} (${item?.hennaType || "Henna type not selected"})`;
        })
        .join(", ");

      navigate("/action-status", {
        replace: true,
        state: {
          status: "success",
          eyebrow: "Booking Confirmed",
          title: "Your appointment has been booked successfully",
          message:
            data?.message ||
            "Your henna appointment has been scheduled successfully.",
          note:
            payload.serviceMode === SERVICE_MODE.STUDIO
              ? "Please arrive on time for your appointment. Payment will be made in cash at the time of service. After your appointment is completed, henna aftercare tips will be sent to your email."
              : "Your mobile appointment has been scheduled successfully. Payment will be made in cash at the time of service, and the client is responsible for the artist’s travel expenses. After your appointment is completed, henna aftercare tips will be sent to your email.",
          details: [
            {
              label: "Confirmation Code",
              value: confirmationCode || "Will be provided by email",
            },
            {
              label: "Booked Services",
              value: bookedServicesText || "—",
            },
            {
              label: "Date",
              value: payload.bookingDate ? formatDate(payload.bookingDate) : "—",
            },
            {
              label: "Time",
              value:
                payload.startTime && payload.endTime
                  ? `${formatTime(payload.startTime)} - ${formatTime(
                      payload.endTime
                    )}`
                  : "—",
            },
            {
              label: "Party Size",
              value: String(payload.partySize || 0),
            },
            {
              label: "Price",
              value: formatCurrency(payload.bookingPrice || 0),
            },
            {
              label: "Duration",
              value: `${payload.bookingDurationMinutes || 0} minutes`,
            },
            {
              label: "Appointment Type",
              value: modeDescription,
            },
            {
              label:
                payload.serviceMode === SERVICE_MODE.STUDIO
                  ? "Studio Location"
                  : "Appointment Address",
              value:
                payload.serviceMode === SERVICE_MODE.STUDIO
                  ? STUDIO_LOCATION
                  : addressText || payload.locationNotes || "—",
            },
            ...(payload.serviceMode === SERVICE_MODE.MOBILE
              ? [
                  {
                    label: "Travel Notice",
                    value: MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
                  },
                ]
              : []),
            ...(bookingItemsWithDetails.some(
              (item) => item?.hennaType === HENNA_TYPES.JAGUAR
            )
              ? [
                  {
                    label: "Jaguar Notice",
                    value: JAGUAR_HENNA_DISCLAIMER,
                  },
                ]
              : []),
            ...(payload.serviceMode === SERVICE_MODE.MOBILE &&
            payload.locationNotes
              ? [
                  {
                    label: "Location Notes",
                    value: payload.locationNotes,
                  },
                ]
              : []),
            ...(guestEmail
              ? [
                  {
                    label: "Confirmation Sent To",
                    value: guestEmail,
                  },
                ]
              : []),
          ],
          actions: [
            {
              label: "Book Another Appointment",
              to: "/booking",
              variant: "primary",
            },
            {
              label: "Back to Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setPageError(err?.message || "We could not complete your booking.");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingServices) {
    return <Loader fullScreen text="Preparing your booking experience..." />;
  }

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="Book an Appointment"
          title="Book your henna service"
          subtitle="Choose one or more services, select an available date and time, and complete your appointment details with confidence."
          className="mb-8"
        />

        {pageError ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{pageError}</p>
          </div>
        ) : null}

        {availabilityMessage ? (
          <div className="mb-6 rounded-2xl border border-[#d7c2a3] bg-[#faf6ef] p-4">
            <div className="flex items-start gap-3">
              <FiInfo className="mt-0.5 text-[#8B3A3A]" size={18} />
              <p className="text-sm text-[#3b2f2f]">{availabilityMessage}</p>
            </div>
          </div>
        ) : null}

        <div className="mb-6 content-card-lg p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#8B3A3A]">
                Booking Option
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {isAuthenticated
                  ? "You may continue as a signed-in client or switch to a guest booking if that feels easier for this appointment."
                  : "You are currently booking as a guest because no active sign-in session was found."}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {isAuthenticated ? (
                <Button
                  variant={
                    bookingOwnerType === BOOKING_OWNER_TYPE.USER
                      ? "primary"
                      : "secondary"
                  }
                  size="sm"
                  onClick={() => {
                    resetMessages();
                    setBookingOwnerType(BOOKING_OWNER_TYPE.USER);
                  }}
                >
                  Signed-In Client
                </Button>
              ) : null}

              <Button
                variant={
                  bookingOwnerType === BOOKING_OWNER_TYPE.GUEST
                    ? "primary"
                    : "secondary"
                }
                size="sm"
                onClick={() => {
                  resetMessages();
                  setBookingOwnerType(BOOKING_OWNER_TYPE.GUEST);
                }}
              >
                Guest Booking
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <BookingForm
              formData={formData}
              errors={errors}
              services={services}
              bookingItemsWithDetails={bookingItemsWithDetails}
              totals={bookingTotals}
              onChange={handleChange}
              onItemChange={handleItemChange}
              onAddService={handleAddService}
              onRemoveService={handleRemoveService}
              onSubmit={handleSubmit}
              onSelectSlot={handleSelectSlot}
              availableDates={availableDates}
              availableSlots={availableSlots}
              slotsLoading={slotsLoading}
              loading={submitting || availabilityLoading}
              isGuest={bookingOwnerType === BOOKING_OWNER_TYPE.GUEST}
              submitLabel={
                bookingOwnerType === BOOKING_OWNER_TYPE.GUEST
                  ? "Submit Guest Booking"
                  : "Submit Booking"
              }
            />
          </div>

          <div className="min-w-0">
            <BookingSummary
              bookingData={formData}
              bookingItemsWithDetails={bookingItemsWithDetails}
              totals={bookingTotals}
              className="sticky-summary"
            />
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default Booking;